import { CACHE_HIT_KEY } from "@faire/retailer-visitor-shared/lib/cacheHitKey";
import { getGlobalProperty } from "@faire/web--source/common/globals/getGlobalProperty";
import { logError } from "@faire/web--source/common/logging";
import { useRef } from "react";

/**
 * Hook to cache `data`. To query from the cache, pass `"CACHE_HIT"` as `data`.
 * @returns the last provided value of `data` that is not `"CACHE_HIT"`.
 */
export const useKeyValueCache = <T>(
  data: T | typeof CACHE_HIT_KEY,
  name: string,
  fallbackFunction?: () => T
): T => {
  const singleEntryCache = useRef(data);
  const cache: Record<string, T> | undefined = getGlobalProperty("cache");

  if (singleEntryCache.current === CACHE_HIT_KEY) {
    if (!cache) {
      throw new Error("Global cache is not available. This should not happen.");
    }
    const cachedData = cache[name];
    if (!cachedData) {
      if (fallbackFunction) {
        logError(
          `useKeyValueCache(${name}): Cache miss, but fallback function is provided. Returning fallback value.`
        );
        const fallbackValue = fallbackFunction();
        cache[name] = fallbackValue;
        return fallbackValue;
      } else {
        throw new Error(
          "Invalid operation: should not read from cache on direct load"
        );
      }
    }
    if (cachedData === CACHE_HIT_KEY) {
      throw new Error("Global cache data is corrupted");
    }
    singleEntryCache.current = cachedData;
  }

  if (data !== CACHE_HIT_KEY) {
    if (cache) {
      cache[name] = data;
    }
    singleEntryCache.current = data;
  }

  return singleEntryCache.current;
};
